import { ChevronDown, ChevronUp } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React, {
  FC,
  KeyboardEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { cn } from '@column/ui';

export type SectionVariantType = 'isCompact';

export interface SectionProps extends PropsWithChildren {
  headline?: ReactNode | string;
  description?: string;
  isDisabled?: boolean;
  isCollapsed?: boolean;
  hide?: boolean;
  notifications?: ReactNode;
  variant?: SectionVariantType | SectionVariantType[];
}

export const Section: FC<SectionProps> = ({
  headline,
  description,
  children,
  notifications,
  variant,
  isCollapsed,
  isDisabled,
  hide,
}) => {
  const [isOpenState, setIsOpenState] = useState<boolean | undefined>(
    isDisabled === true ? false : typeof isCollapsed === 'boolean' ? !isCollapsed : undefined
  );
  // Clip the content only while it animates, so focus rings and shadows aren't cut off once settled.
  // Tracked in state because motion leaves the animated `overflow` applied rather than honoring `transitionEnd`.
  const [isAnimating, setIsAnimating] = useState(false);

  const isToggleable = typeof isCollapsed !== 'undefined' && isDisabled !== true;

  const handleOpenToggle = useCallback(() => {
    if (!isToggleable) {
      return;
    }

    setIsOpenState((s) => !s);
  }, [isToggleable]);

  const handleHeaderKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
        return;
      }
      event.preventDefault();
      handleOpenToggle();
    },
    [handleOpenToggle]
  );

  useEffect(() => {
    if (isDisabled === false) {
      setIsOpenState(true);
    } else if (isDisabled === true) {
      setIsOpenState(false);
    }
  }, [isDisabled]);

  useEffect(() => {
    if (hide === false) {
      setIsOpenState(true);
    } else if (hide === true) {
      setIsOpenState(false);
    }
  }, [hide]);

  const isCompact = useMemo(
    () => (typeof variant === 'string' ? variant === 'isCompact' : (variant?.includes('isCompact') ?? false)),
    [variant]
  );
  const isOpen = isOpenState !== false;
  const isMultiColumn = !!notifications;
  const showChevron =
    typeof isOpenState !== 'undefined' && typeof isDisabled === 'undefined' && typeof isCollapsed !== 'undefined';

  return (
    <div
      className={cn(
        'section @container/section-wrapper box-border grid grid-cols-[100%] items-start justify-start',
        'px-(--page-margin) py-6 shadow-[0_-1px_0_var(--color-border)] first-of-type:shadow-none',
        isOpen && !isCompact ? 'gap-6' : 'gap-0',
        hide && 'py-0 shadow-none',
        isCompact && 'p-0 shadow-none'
      )}
    >
      {headline && !hide && (
        <div
          className={cn(
            'group/section-header items-start gap-2 rounded-md transition-opacity duration-200 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 @tablet/section-wrapper:gap-6',
            isMultiColumn ? 'grid grid-cols-[auto] @tablet/section-wrapper:grid-cols-[minmax(0,1fr)_auto]' : 'flex',
            isDisabled && 'opacity-50',
            typeof isCollapsed !== 'undefined' && 'cursor-pointer'
          )}
          onClick={handleOpenToggle}
          {...(isToggleable && {
            role: 'button',
            tabIndex: 0,
            'aria-expanded': isOpen,
            onKeyDown: handleHeaderKeyDown,
          })}
        >
          <div className="flex min-w-0 flex-1 items-start gap-4">
            {showChevron && (
              // A 24px box around the 16px icon; the negative margins keep the icon where it sat without one.
              <span className="-my-0.5 -mr-1 -ml-1.5 flex size-6 shrink-0 items-center justify-center rounded-md border border-transparent text-foreground transition-colors duration-150 group-hover/section-header:border-gray-200 dark:group-hover/section-header:border-white/15">
                {isOpenState ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
              </span>
            )}

            <div className="flex flex-1 items-start gap-1">
              <div className="grid justify-start gap-1">
                <h2 className="m-0 max-w-[36ch] text-base leading-5 font-medium text-foreground [&_small]:align-baseline [&_small]:text-muted-foreground">
                  {headline}
                </h2>
                {description && <div className="text-xs leading-[18px] text-muted-foreground">{description}</div>}
              </div>
            </div>
          </div>

          {notifications}
        </div>
      )}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            className={cn(isAnimating && 'overflow-hidden')}
          >
            <div className={cn('grid', isCompact ? 'gap-4' : 'gap-6')}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
